import { useEffect, useRef, type ReactElement } from "react";
import { authProductMark, SignInStartFailureScreen } from "./auth-screens.js";
import { takeReturnTo } from "./auth-tab-storage.js";
import { useSignInStart } from "./sign-in-start.js";

/**
 * Mounted on `/auth/restart`, OUTSIDE the page guard (PB-5461, CONTRACT §4).
 * The Keycloak login page sends the tab here to start a fresh sign-in, for
 * example when its own form has been open too long. It starts a sign-in to the
 * page this tab kept before it first left for Keycloak, or Home when the tab
 * kept none, and goes straight back to Keycloak. The person is in this tab's
 * Keycloak page, so it takes the one-tab sign-in lease rather than electing
 * (d21).
 *
 * Starting a sign-in on load is safe here, unlike signing out: the worst a
 * link to this route can do is show someone the sign-in page.
 */
export function RestartSignInPage(): ReactElement {
  const signIn = useSignInStart();
  const { start } = signIn;
  // StrictMode runs the effect twice; the kept page is read (and removed)
  // once, and `beginLogin` keeps it again for the round trip.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    start(takeReturnTo() ?? "/");
  }, [start]);

  if (signIn.failure) {
    return (
      <SignInStartFailureScreen
        failure={signIn.failure}
        onTryAgain={signIn.retry}
      />
    );
  }

  /* Kept as plain text, like the guard's waiting line (OK-38). */
  return (
    <div className="pb-motion-content-enter flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
      {authProductMark}
      <p>Redirecting to sign in…</p>
    </div>
  );
}
